'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import storageService, {
  type SessionFilesSummary,
  formatFileSize,
} from '@/services/storage-service';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

interface DeleteSessionDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The session ID */
  sessionId: string;
  /** The session title for display */
  sessionTitle: string;
  /** Callback when delete is confirmed */
  onConfirm: (deleteFiles: boolean) => Promise<void>;
}

/**
 * Confirmation dialog for deleting a research session.
 * Shows option to also delete associated files if any exist.
 */
export function DeleteSessionDialog({
  isOpen,
  onClose,
  sessionId,
  sessionTitle,
  onConfirm,
}: DeleteSessionDialogProps) {
  const t = useTranslations('DeleteSessionDialog');
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filesSummary, setFilesSummary] = useState<SessionFilesSummary | null>(
    null
  );
  const [deleteFiles, setDeleteFiles] = useState(true); // Default ON

  // Fetch files summary when dialog opens
  useEffect(() => {
    if (isOpen && sessionId) {
      setIsLoading(true);
      setFilesSummary(null);
      setDeleteFiles(true);

      const fetchSummary = async () => {
        try {
          const response =
            await storageService.getSessionFilesSummary(sessionId);
          if (response.status === 'success' && response.data) {
            setFilesSummary(response.data);
          }
        } catch (error) {
          console.error('Error fetching session files summary:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchSummary();
    }
  }, [isOpen, sessionId]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm(deleteFiles && (filesSummary?.file_count ?? 0) > 0);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasFiles = (filesSummary?.file_count ?? 0) > 0;

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className='flex items-center gap-2'>
            <AlertTriangle className='h-5 w-5 text-status-error' />
            {t('title')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='space-y-3'>
              <p>
                {t('description', {
                  title:
                    sessionTitle.length > 50
                      ? `${sessionTitle.substring(0, 50)}...`
                      : sessionTitle,
                })}
              </p>

              {isLoading ? null : hasFiles ? (
                <div className='mt-4 p-3 rounded-lg bg-surface-muted border border-line-default'>
                  <div className='flex items-start gap-3'>
                    <FileText className='h-5 w-5 text-content-secondary flex-shrink-0 mt-0.5' />
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-content-primary'>
                        {t('filesFound', {
                          count: filesSummary?.file_count ?? 0,
                        })}
                      </p>
                      <p className='text-xs text-content-muted mt-0.5'>
                        {t('totalSize', {
                          size: formatFileSize(filesSummary?.total_bytes ?? 0),
                        })}
                      </p>
                    </div>
                  </div>

                  <div className='mt-3 flex items-center gap-2'>
                    <Checkbox
                      id='delete-files'
                      checked={deleteFiles}
                      onCheckedChange={(checked) =>
                        setDeleteFiles(checked === true)
                      }
                    />
                    <Label
                      htmlFor='delete-files'
                      className='text-sm font-normal cursor-pointer'
                    >
                      {t('deleteFilesCheckbox')}
                    </Label>
                  </div>
                </div>
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading || isDeleting}
            className='bg-status-error hover:bg-status-error/90 focus:ring-status-error'
          >
            {isDeleting ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                {t('deleting')}
              </>
            ) : (
              t('delete')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
